/**
 * Store Locator / Retailer Finder
 * Vanilla JavaScript implementation (converted from jQuery)
 * Features:
 * - Google Maps integration with custom markers
 * - Search by postal code with geocoding
 * - Distance filter with range slider
 * - Google Sheets + App API data sync
 * - Set preferred store integration
 */

(function() {
  'use strict';

  // Function to initialize the retailer
  function initRetailer() {
    const storeMapElement = document.querySelector('.store_map');
    if (!storeMapElement) return;

    // Parse settings from JSON script tag
    const settingsElement = document.querySelector('[data-retailer-settings]');
    if (!settingsElement) {
      console.error('Retailer settings not found');
      return;
    }

    const settings = JSON.parse(settingsElement.textContent);
    
    // Initialize the retailer with settings
    new RetailerMap(storeMapElement, settings);
  }

  // RetailerMap class - Main controller for the store locator
  class RetailerMap {
    constructor(mapElement, settings) {
      this.mapElement = mapElement;
      this.settings = settings;
      this.stores = null;
      this.storesHeader = null;
      this.map = null;
      this.geocoder = null;
      this.infoWindow = null;
      this.distRadiusInput = settings.range_default || 50;
      this.markers = [];
      
      // Default map center (Montreal)
      this.defaultCenter = { lat: 45.5016889, lng: -73.567256 };
      
      // Store skeleton template for loading state
      this.skeletonTemplate = document.querySelector('.store_sidebar')?.innerHTML || '';
      
      this.init();
    }

    init() {
      // Initialize Google Maps
      this.initMap();
      
      // Initialize range slider
      this.initRangeSlider();
      
      // Bind event listeners
      this.bindEvents();
      
      // Fetch stores from Google Sheets
      this.fetchStores();
    }

    initMap() {
      // Google Maps style (grayscale theme)
      const mapStyles = [
        {"featureType":"water","elementType":"geometry","stylers":[{"color":"#e9e9e9"},{"lightness":17}]},
        {"featureType":"landscape","elementType":"geometry","stylers":[{"color":"#f5f5f5"},{"lightness":20}]},
        {"featureType":"road.highway","elementType":"geometry.fill","stylers":[{"color":"#ffffff"},{"lightness":17}]},
        {"featureType":"road.highway","elementType":"geometry.stroke","stylers":[{"color":"#ffffff"},{"lightness":29},{"weight":0.2}]},
        {"featureType":"road.arterial","elementType":"geometry","stylers":[{"color":"#ffffff"},{"lightness":18}]},
        {"featureType":"road.local","elementType":"geometry","stylers":[{"color":"#ffffff"},{"lightness":16}]},
        {"featureType":"poi","elementType":"geometry","stylers":[{"color":"#f5f5f5"},{"lightness":21}]},
        {"featureType":"poi.park","elementType":"geometry","stylers":[{"color":"#dedede"},{"lightness":21}]},
        {"elementType":"labels.text.stroke","stylers":[{"visibility":"on"},{"color":"#ffffff"},{"lightness":16}]},
        {"elementType":"labels.text.fill","stylers":[{"saturation":36},{"color":"#333333"},{"lightness":40}]},
        {"elementType":"labels.icon","stylers":[{"visibility":"off"}]},
        {"featureType":"transit","elementType":"geometry","stylers":[{"color":"#f2f2f2"},{"lightness":19}]},
        {"featureType":"administrative","elementType":"geometry.fill","stylers":[{"color":"#fefefe"},{"lightness":20}]},
        {"featureType":"administrative","elementType":"geometry.stroke","stylers":[{"color":"#fefefe"},{"lightness":17},{"weight":1.2}]}
      ];

      this.map = new google.maps.Map(this.mapElement, {
        center: this.defaultCenter,
        zoom: 9,
        mapTypeControl: false,
        streetViewControl: false,
        styles: mapStyles
      });

      this.geocoder = new google.maps.Geocoder();
      this.infoWindow = new google.maps.InfoWindow();
    }

    initRangeSlider() {
      const slider = document.querySelector('.range-slider');
      if (!slider) return;

      const range = slider.querySelector('.range-slider__range');
      const value = slider.querySelector('.range-slider__value');

      if (!range || !value) return;

      // Set initial value
      const initialValue = range.value;
      const kmUnit = this.settings.i18n?.km_unit || 'km';
      value.textContent = initialValue + kmUnit;
      this.distRadiusInput = parseInt(initialValue, 10);

      // Update on input
      range.addEventListener('input', () => {
        value.textContent = range.value + kmUnit;
        this.distRadiusInput = parseInt(range.value, 10);
        
        // Auto-filter stores in real-time if a search has been performed
        const zipCodeInput = document.getElementById('zip_code');
        if (zipCodeInput && zipCodeInput.value.trim() !== '') {
          this.searchStores();
        }
      });
    }

    bindEvents() {
      // Search button click
      const searchButton = document.querySelector('.store_search');
      if (searchButton) {
        searchButton.addEventListener('click', () => this.searchStores());
      }

      // Enter key on search input
      const zipCodeInput = document.getElementById('zip_code');
      if (zipCodeInput) {
        zipCodeInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter' || e.which === 13) {
            this.searchStores();
          }
        });
      }

      // Store info click (click on sidebar item to center map)
      document.addEventListener('click', (e) => {
        const storeInfo = e.target.closest('.store_info');
        if (storeInfo && !storeInfo.classList.contains('store_info--skeleton')) {
          const targetLat = parseFloat(storeInfo.dataset.targetLat);
          const targetLng = parseFloat(storeInfo.dataset.targetLng);
          
          if (!isNaN(targetLat) && !isNaN(targetLng)) {
            const latLng = new google.maps.LatLng(targetLat, targetLng);
            this.map.setCenter(latLng);
            this.map.setZoom(14);
          }
        }
      });
    }

    async fetchStores() {
      if (!this.settings.doc_id || !this.settings.api_key) {
        console.error('Missing Google Sheets doc_id or api_key');
        return;
      }

      const url = `https://sheets.googleapis.com/v4/spreadsheets/${this.settings.doc_id}/values/Feuille 1?key=${this.settings.api_key}`;

      try {
        const response = await fetch(url);
        const result = await response.json();
        
        this.stores = result.values;
        this.storesHeader = this.stores[0].map(name => name.toLowerCase());
        
        // Remove header row
        this.stores.splice(0, 1);
        
        console.log('📊 Fetched from Google Sheets:', this.stores.length, 'stores');
        
        // Sync with app API to get shopify_id and searchable_only flags
        await this.syncWithAppAPI();
        
      } catch (error) {
        console.error('Error fetching stores from Google Sheets:', error);
      }
    }

    async syncWithAppAPI() {
      const appUrl = '/apps/popshoesinventories/location_by_coordinates/all_locations.json';
      
      try {
        const response = await fetch(appUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            locations: this.stores,
            keys: this.storesHeader
          })
        });

        const locations = await response.json();
        this.stores = locations;
        this.storesHeader.push('store_details');
        
        console.log('✅ Stores loaded:', this.stores.length, 'stores');
        
        // Now that we have all data, add markers and render stores
        this.setStores();
        this.renderClosestStores(this.stores);
        
      } catch (error) {
        console.error('Error syncing with app API:', error);
        // Still render what we have from Google Sheets
        this.setStores();
        this.renderClosestStores(this.stores);
      }
    }

    setStores() {
      if (!this.stores || this.stores.length === 0) {
        setTimeout(() => this.setStores(), 500);
        return;
      }

      this.stores.forEach(store => {
        this.markStore(store);
      });
    }

    markStore(storeInfo) {
      const lat = parseFloat(this.getAPIField('latitude', storeInfo));
      const lng = parseFloat(this.getAPIField('longitude', storeInfo));

      if (isNaN(lat) || isNaN(lng)) return;

      // Determine marker icon
      let iconConfig = null;
      const customIcon = this.getAPIField('icone', storeInfo);
      const baseIcon = this.settings.marker;
      
      let iconUrl = '';
      if (customIcon) {
        iconUrl = `https://cdn.shopify.com/s/files/1/0460/6260/0355/files/${customIcon}`;
      } else if (baseIcon) {
        iconUrl = baseIcon;
      }

      if (iconUrl) {
        iconConfig = {
          url: iconUrl,
          scaledSize: new google.maps.Size(55, 60),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(27.5, 0)
        };
      }

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(lat, lng),
        map: this.map,
        icon: iconConfig
      });

      this.markers.push(marker);

      // Show info window on marker click
      marker.addListener('click', () => {
        this.showStoreInfo(storeInfo, marker);
      });
    }

    showStoreInfo(storeInfo, marker) {
      const directionLabel = this.settings.i18n?.direction || 'Get directions';
      const entreprise = this.getAPIField('entreprise', storeInfo);
      const adresse = this.getAPIField('adresse', storeInfo);
      const openingHours = this.getAPIField('opening hours', storeInfo);

      const title = `<span class='gm-info-title'><strong>${entreprise}</strong></span>`;
      const address = `<p class="gm-info-desc">${adresse}</p>`;
      const directions = `<a class='directions_link' href='https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entreprise + ' ' + adresse)}' target='_blank'>${directionLabel}</a>`;
      
      let openingHoursHTML = '';
      if (openingHours) {
        const rows = openingHours.split(',').map(segment => {
          const match = segment.trim().match(/^(.+?):\s*(.+)$/);
          if (!match) return '';
          return `<tr style="border: none;"><td style="border: none;"><strong>${match[1]}</strong></td><td style="border: none; padding-left:12px;">${match[2].trim()}</td></tr>`;
        }).join('');
        openingHoursHTML = `<div class='opening-hours' style='margin-top: 15px;'><table style="border-collapse: collapse;">${rows}</table></div>`;
      }

      const content = [title, address, directions, openingHoursHTML].join('');
      
      this.infoWindow.setContent(content);
      this.infoWindow.open(this.map, marker);
    }

    searchStores() {
      const zipCodeInput = document.getElementById('zip_code');
      if (!zipCodeInput) return;

      const zipCode = zipCodeInput.value.trim();
      if (!zipCode) return;

      console.log('🔍 Searching stores near:', zipCode, '| Radius:', this.distRadiusInput, 'km');

      // Show skeleton loaders
      const sidebar = document.querySelector('.store_sidebar');
      if (sidebar) {
        sidebar.innerHTML = this.skeletonTemplate;
        sidebar.scrollTop = 0;
        sidebar.style.display = 'block';
      }

      // Geocode the postal code
      this.geocoder.geocode({
        address: zipCode,
        region: 'ca'
      }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const userLat = results[0].geometry.location.lat();
          const userLng = results[0].geometry.location.lng();

          // Calculate distances and filter stores
          const closeStores = [];
          
          this.stores.forEach(store => {
            const storeLat = parseFloat(this.getAPIField('latitude', store));
            const storeLng = parseFloat(this.getAPIField('longitude', store));
            const dist = this.calculateDistance(userLat, userLng, storeLat, storeLng, 'K');
            
            // Add distance to store object
            store.distance = dist;
            
            if (dist <= this.distRadiusInput) {
              closeStores.push(store);
            }
          });

          console.log('📍 Found', closeStores.length, 'stores within', this.distRadiusInput, 'km');

          this.renderClosestStores(closeStores);

          // Center map and fit bounds
          const myLatLng = new google.maps.LatLng(userLat, userLng);
          const circleOptions = {
            center: myLatLng,
            fillOpacity: 0,
            strokeOpacity: 0,
            map: this.map,
            radius: this.distRadiusInput * 1000
          };

          const myCircle = new google.maps.Circle(circleOptions);
          this.map.fitBounds(myCircle.getBounds());
          this.map.setCenter(results[0].geometry.location);

        } else {
          const noResult = this.settings.i18n?.no_result || 'No results found';
          alert(noResult);
        }
      });
    }

    renderClosestStores(stores) {
      console.log('🏪 Rendering stores:', stores.length, 'stores | First has distance?', stores[0]?.distance !== undefined);
      
      // Sort stores - by distance if available, otherwise alphabetically by name
      stores.sort((a, b) => {
        // If distance exists, sort by distance
        if (a.distance !== undefined && b.distance !== undefined) {
          if (a.distance > b.distance) return 1;
          if (a.distance < b.distance) return -1;
          return 0;
        }
        
        // Otherwise, sort alphabetically by entreprise name
        const nameA = this.getAPIField('entreprise', a)?.toLowerCase() || '';
        const nameB = this.getAPIField('entreprise', b)?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });

      const directionLabel = this.settings.i18n?.direction || 'Get directions';
      const setPrefStoreLabel = this.settings.i18n?.set_preferred_store || 'Set as preferred store';
      
      let template = '';

      stores.forEach(store => {
        const entreprise = this.getAPIField('entreprise', store);
        const adresse = this.getAPIField('adresse', store);
        const extras = this.getAPIField('extras', store);
        const latitude = this.getAPIField('latitude', store);
        const longitude = this.getAPIField('longitude', store);
        const storeDetails = this.getAPIField('store_details', store);

        // Build the "Set as preferred store" button if store has shopify_id
        let preflocButton = '';
        if (storeDetails && typeof storeDetails === 'object') {
          const storeId = storeDetails.shopify_id !== null ? storeDetails.shopify_id : storeDetails.id;
          const searchableOnly = storeDetails.searchable_only;

          if (storeId !== undefined && searchableOnly !== undefined) {
            preflocButton = `
              <div class="prefloc_cta" 
                   data-shopify-id="${storeId}" 
                   data-searchable-only="${searchableOnly}">
                ${setPrefStoreLabel}
              </div>
            `;
          }
        }

        // Build the store info HTML with button inside
        template += `
          <div class="store_info" data-target-lat="${latitude}" data-target-lng="${longitude}">
            <h3>${entreprise}</h3>
            <p class="adr">${adresse}</p>
            <p>${extras}</p>
            <a class='directions_link' href='https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entreprise + ' ' + adresse)}' target='_blank'>${directionLabel}</a>
            ${preflocButton}
          </div>
        `;
      });

      if (stores.length === 0) {
        const noResult = this.settings.i18n?.no_result || 'No results found';
        template = `<div class="store_info">${noResult}</div>`;
      }

      const sidebar = document.querySelector('.store_sidebar');
      if (sidebar) {
        // Fade out effect
        sidebar.style.opacity = '0';
        
        setTimeout(() => {
          sidebar.innerHTML = template;
          sidebar.scrollTop = 0;
          
          // Bind preferred location CTAs
          this.bindPreflocCtas();
          
          // Fade in effect
          sidebar.style.opacity = '1';
        }, 300);
      }
    }

    bindPreflocCtas() {
      const ctas = document.querySelectorAll('.store_sidebar .prefloc_cta');
      ctas.forEach(cta => {
        cta.addEventListener('click', (e) => this.persistPreferredLocation(e));
      });
    }

    persistPreferredLocation(e) {
      e.stopPropagation();
      
      const shopifyId = e.currentTarget.dataset.shopifyId;
      const searchableOnly = e.currentTarget.dataset.searchableOnly;

      if (shopifyId && searchableOnly !== undefined) {
        localStorage.setItem('preferred_location_id', shopifyId);
        localStorage.setItem('preferred_location_searchable_only', searchableOnly);
        
        // Dispatch event to notify other components
        const event = new Event('set_preferred_location_id_event_by_map');
        window.dispatchEvent(event);
        
        // Visual feedback
        const buttonElement = e.currentTarget; // Save reference before setTimeout
        buttonElement.textContent = '✓ ' + (this.settings.i18n?.set_preferred_store || 'Set as preferred store');
        buttonElement.style.backgroundColor = '#1b8b1b';
        
        setTimeout(() => {
          buttonElement.textContent = this.settings.i18n?.set_preferred_store || 'Set as preferred store';
          buttonElement.style.backgroundColor = '';
        }, 2000);
      }
    }

    getAPIField(name, obj) {
      const index = this.storesHeader.indexOf(name);
      if (index !== -1 && obj[index] !== undefined) {
        return obj[index];
      }
      return '';
    }

    calculateDistance(lat1, lon1, lat2, lon2, unit) {
      if ((lat1 === lat2) && (lon1 === lon2)) {
        return 0;
      }

      const radlat1 = Math.PI * lat1 / 180;
      const radlat2 = Math.PI * lat2 / 180;
      const theta = lon1 - lon2;
      const radtheta = Math.PI * theta / 180;
      
      let dist = Math.sin(radlat1) * Math.sin(radlat2) + 
                 Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
      
      if (dist > 1) {
        dist = 1;
      }
      
      dist = Math.acos(dist);
      dist = dist * 180 / Math.PI;
      dist = dist * 60 * 1.1515;
      
      if (unit === 'K') {
        dist = dist * 1.609344; // Convert to kilometers
      }
      if (unit === 'N') {
        dist = dist * 0.8684; // Convert to nautical miles
      }
      
      return dist;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRetailer);
  } else {
    initRetailer();
  }

})();
